import { OrcamentoIcon, OrcamentoFeedbackProvider, useOrcamentoFeedback } from '../components/OrcamentoUI';
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './FichaLevantamento.css';

const AdminFichasContent = () => {
    const { confirm, prompt } = useOrcamentoFeedback();
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchTemplates();
    }, []);

    const handleTemplateSelectionId = (templateId, allTemplates = templates) => {
        const t = allTemplates.find(x => x.id == templateId);
        if (!t) return;

        const clonedTemplate = JSON.parse(JSON.stringify(t));

        // Migrate template old "type" to new booleans
        if (clonedTemplate.sections) {
            clonedTemplate.sections.forEach(sec => {
                if (sec.questions) {
                    sec.questions.forEach(q => {
                        if (q.type) {
                            q.show_sn = q.type.includes('sn_');
                            q.show_quant = q.type.includes('_qtd_') || q.type.startsWith('number_');
                            q.show_un = q.type.includes('_unid_');
                            q.show_m2 = q.type.includes('_m2_');
                            q.show_ml = q.type.includes('_ml_');
                            q.show_obs = q.type.includes('_obs') || q.type === 'obs';
                            q.show_largura = false;
                            q.show_altura = false;
                            q.show_dropdown = false;
                            q.dropdown_options = '';
                            q.is_multiplier = false;
                            delete q.type;
                        }
                    });
                }
            });
        }

        setSelectedTemplate(clonedTemplate);
        setMessage({ type: '', text: '' });
    };

    const fetchTemplates = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/fichas-levantamento/templates');
            setTemplates(res.data);
            if (res.data.length > 0 && !selectedTemplate) {
                handleTemplateSelectionId(res.data[0].id, res.data);
            } else if (selectedTemplate) {
                const stillExists = res.data.find(t => t.id === selectedTemplate.id);
                if (stillExists) handleTemplateSelectionId(selectedTemplate.id, res.data);
                else if (res.data.length > 0) handleTemplateSelectionId(res.data[0].id, res.data);
                else setSelectedTemplate(null);
            }
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Erro ao carregar templates.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateTemplate = async () => {
        const nome = await prompt("Qual o nome para o novo template de ficha?", { title: "Novo template", label: "Nome" });
        if (!nome || nome.trim() === '') return;

        try {
            const res = await api.post('/fichas-levantamento/templates', {
                nome,
                sections: []
            });
            setMessage({ type: 'success', text: 'Template criado com sucesso!' });
            const newTemplates = [...templates, res.data];
            setTemplates(newTemplates);
            handleTemplateSelectionId(res.data.id, newTemplates);
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Erro ao criar template.' });
        }
    };

    const handleRenameTemplate = async () => {
        if (!selectedTemplate) return;
        const nome = await prompt("Qual o novo nome para este template?", { title: "Renomear template", label: "Nome", defaultValue: selectedTemplate.nome });
        if (!nome || nome.trim() === '' || nome === selectedTemplate.nome) return;

        setSelectedTemplate({ ...selectedTemplate, nome });
    };

    const handleDeleteTemplate = async () => {
        if (!selectedTemplate) return;
        if (!await confirm(`Tem a certeza absoluta de que quer apagar o template "${selectedTemplate.nome}"?\n\nAtenção: As fichas já submetidas com este template não perderão os dados, mas não poderá voltar a configurá-lo.`)) return;

        try {
            await api.delete(`/fichas-levantamento/templates/${selectedTemplate.id}`);
            setMessage({ type: 'success', text: 'Template apagado com sucesso!' });
            setSelectedTemplate(null);
            fetchTemplates();
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Erro ao apagar template.' });
        }
    };

    const handleSaveTemplate = async () => {
        if (!selectedTemplate) return;
        try {
            await api.put(`/fichas-levantamento/templates/${selectedTemplate.id}`, selectedTemplate);
            setMessage({ type: 'success', text: 'Template atualizado com sucesso!' });
            fetchTemplates();
        } catch (err) {
            console.error(err);
            setMessage({ type: 'error', text: 'Erro ao guardar template.' });
        }
    };

    const addSection = () => {
        const newSecs = [...(selectedTemplate.sections || []), { title: 'Nova Secção', questions: [] }];
        setSelectedTemplate({ ...selectedTemplate, sections: newSecs });
    };

    const updateSection = (sIdx, title) => {
        const newSecs = [...selectedTemplate.sections];
        newSecs[sIdx].title = title;
        setSelectedTemplate({ ...selectedTemplate, sections: newSecs });
    };

    const removeSection = (sIdx) => {
        const newSecs = selectedTemplate.sections.filter((_, i) => i !== sIdx);
        setSelectedTemplate({ ...selectedTemplate, sections: newSecs });
    };

    const addQuestion = (sIdx) => {
        const newSecs = [...selectedTemplate.sections];
        if (!newSecs[sIdx].questions) newSecs[sIdx].questions = [];

        newSecs[sIdx].questions.push({
            id: 'q_' + Date.now() + '_' + Math.floor(Math.random() * 100),
            label: 'Nova Pergunta',
            show_sn: false,
            show_quant: false,
            show_un: false,
            show_m2: false,
            show_ml: false,
            show_largura: false,
            show_altura: false,
            show_dropdown: false,
            dropdown_options: '',
            show_obs: true,
            is_multiplier: false
        });
        setSelectedTemplate({ ...selectedTemplate, sections: newSecs });
    };

    const updateQuestion = (sIdx, qIdx, field, value) => {
        const newSecs = [...selectedTemplate.sections];
        newSecs[sIdx].questions[qIdx][field] = value;
        setSelectedTemplate({ ...selectedTemplate, sections: newSecs });
    };

    const removeQuestion = (sIdx, qIdx) => {
        const newSecs = [...selectedTemplate.sections];
        newSecs[sIdx].questions = newSecs[sIdx].questions.filter((_, i) => i !== qIdx);
        setSelectedTemplate({ ...selectedTemplate, sections: newSecs });
    };

    const moveSection = (sIdx, dir) => {
        const newSecs = [...selectedTemplate.sections];
        if (dir === 'up' && sIdx > 0) {
            const temp = newSecs[sIdx];
            newSecs[sIdx] = newSecs[sIdx - 1];
            newSecs[sIdx - 1] = temp;
        } else if (dir === 'down' && sIdx < newSecs.length - 1) {
            const temp = newSecs[sIdx];
            newSecs[sIdx] = newSecs[sIdx + 1];
            newSecs[sIdx + 1] = temp;
        }
        setSelectedTemplate({ ...selectedTemplate, sections: newSecs });
    };

    const moveQuestion = (sIdx, qIdx, dir) => {
        const newSecs = [...selectedTemplate.sections];
        const questions = [...newSecs[sIdx].questions];
        if (dir === 'up' && qIdx > 0) {
            const temp = questions[qIdx];
            questions[qIdx] = questions[qIdx - 1];
            questions[qIdx - 1] = temp;
        } else if (dir === 'down' && qIdx < questions.length - 1) {
            const temp = questions[qIdx];
            questions[qIdx] = questions[qIdx + 1];
            questions[qIdx + 1] = temp;
        }
        newSecs[sIdx].questions = questions;
        setSelectedTemplate({ ...selectedTemplate, sections: newSecs });
    };

    if (user?.role !== 'admin' && user?.role !== 'gerente') {
        return <div style={{ padding: '20px' }}>Não tem permissão para aceder a esta página.</div>;
    }

    return (
        <div className="admin-fichas-wrapper admin-theme-page">
            <div className="top-bar">
                <div className="top-bar-left">
                    <div className="top-bar-title">
                        <h1><OrcamentoIcon name="forms" fallback="⚙️" /> Gestão de Questionários</h1>
                        <p>Configure os formulários e templates de recolha</p>
                    </div>
                </div>
            </div>

            <div className="page-content" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
                <div className="fl-content-wrapper">
                    {message.text && (
                        <div className={`fl-message ${message.type}`}>
                            {message.text}
                        </div>
                    )}

                    {isLoading ? <p>A carregar...</p> : (
                        <div>
                            <div className="fl-form-group">
                                <label>Selecionar Template</label>
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                    <div style={{ flex: 1 }}>
                                        <select
                                            className="fl-val-input"
                                            aria-label="Selecionar template" value={selectedTemplate?.id || ''}
                                            onChange={(e) => handleTemplateSelectionId(e.target.value)}
                                        >
                                            {templates.map(t => (
                                                <option key={t.id} value={t.id}>{t.nome}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <button className="btn-secondary" style={{ padding: '8px 15px', margin: 0 }} onClick={handleCreateTemplate}>
                                        + Novo Template
                                    </button>
                                </div>
                            </div>

                            {selectedTemplate && (
                                <div style={{ marginTop: '30px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                                        <div data-liquid-actions="true" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <h3 style={{ margin: 0 }}>Configuração: {selectedTemplate.nome}</h3>
                                            <button className="btn-secondary" style={{ fontSize: '12px', padding: '4px 8px', margin: 0 }} onClick={handleRenameTemplate} title="Renomear Template"><OrcamentoIcon name="edit" fallback="✏️" /></button>
                                            <button className="btn-danger" style={{ fontSize: '12px', padding: '4px 8px', margin: 0 }} onClick={handleDeleteTemplate} title="Apagar Template"><OrcamentoIcon name="trash" fallback="🗑️" /></button>
                                        </div>
                                        <button className="fl-submit-btn" style={{ width: 'auto', marginTop: 0, padding: '10px 20px' }} onClick={handleSaveTemplate}>
                                            Guardar Configurações
                                        </button>
                                    </div>

                                    <button className="btn-secondary" onClick={addSection} style={{ marginBottom: '20px' }}>
                                        + Adicionar Secção
                                    </button>

                                    <div className="fl-checklist-section">
                                        {selectedTemplate.sections?.map((section, sIdx) => (
                                            <div key={sIdx} className="fl-section-block">
                                                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                                                    <div data-liquid-actions="true" style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                        <button type="button" className="admin-move" aria-label="Subir secção" onClick={() => moveSection(sIdx, 'up')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 5px', fontSize: '10px' }} disabled={sIdx === 0}>▲</button>
                                                        <button type="button" className="admin-move" aria-label="Descer secção" onClick={() => moveSection(sIdx, 'down')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 5px', fontSize: '10px' }} disabled={sIdx === (selectedTemplate.sections.length - 1)}>▼</button>
                                                    </div>
                                                    <input
                                                        className="fl-val-input"
                                                        aria-label={`Título da secção ${sIdx + 1}`} value={section.title}
                                                        onChange={(e) => updateSection(sIdx, e.target.value)}
                                                        style={{ fontWeight: 'bold', fontSize: '16px', flex: 1 }}
                                                    />
                                                    <button className="remove-photo-btn" style={{ position: 'static', margin: 'auto 0' }} aria-label="Apagar secção" onClick={() => removeSection(sIdx)}><OrcamentoIcon name="trash" fallback="X" /></button>
                                                </div>

                                                <div className="fl-questions-list">
                                                    {section.questions?.map((q, qIdx) => (
                                                        <div key={q.id} className="admin-question-row" style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'flex-start' }}>
                                                            <div data-liquid-actions="true" style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '5px' }}>
                                                                <button type="button" className="admin-move" aria-label="Subir pergunta" onClick={() => moveQuestion(sIdx, qIdx, 'up')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 5px', fontSize: '10px' }} disabled={qIdx === 0}>▲</button>
                                                                <button type="button" className="admin-move" aria-label="Descer pergunta" onClick={() => moveQuestion(sIdx, qIdx, 'down')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 5px', fontSize: '10px' }} disabled={qIdx === (section.questions.length - 1)}>▼</button>
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                                                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                                                    <input
                                                                        className="fl-val-input"
                                                                        aria-label={`Pergunta ${qIdx + 1} da secção ${sIdx + 1}`} value={q.label}
                                                                        onChange={(e) => updateQuestion(sIdx, qIdx, 'label', e.target.value)}
                                                                        style={{ flex: 1 }}
                                                                    />
                                                                    <button className="remove-photo-btn" style={{ position: 'static', margin: 0 }} aria-label="Apagar pergunta" onClick={() => removeQuestion(sIdx, qIdx)}><OrcamentoIcon name="trash" fallback="X" /></button>
                                                                </div>
                                                                <div className="admin-question-options" style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', padding: '10px', backgroundColor: '#f7fafc', borderRadius: '6px', fontSize: '13px', alignItems: 'center' }}>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={!!q.show_sn} onChange={(e) => updateQuestion(sIdx, qIdx, 'show_sn', e.target.checked)} /> Sim/Não
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={!!q.show_quant} onChange={(e) => updateQuestion(sIdx, qIdx, 'show_quant', e.target.checked)} /> Quant.
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={!!q.show_un} onChange={(e) => updateQuestion(sIdx, qIdx, 'show_un', e.target.checked)} /> Un.
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={!!q.show_m2} onChange={(e) => updateQuestion(sIdx, qIdx, 'show_m2', e.target.checked)} /> m²
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={!!q.show_ml} onChange={(e) => updateQuestion(sIdx, qIdx, 'show_ml', e.target.checked)} /> m/L
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={!!q.show_largura} onChange={(e) => updateQuestion(sIdx, qIdx, 'show_largura', e.target.checked)} /> Largura
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={!!q.show_altura} onChange={(e) => updateQuestion(sIdx, qIdx, 'show_altura', e.target.checked)} /> Altura
                                                                    </label>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={!!q.show_dropdown} onChange={(e) => updateQuestion(sIdx, qIdx, 'show_dropdown', e.target.checked)} /> Lista
                                                                    </label>
                                                                    {q.show_dropdown && (
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Opção1, Opção2..."
                                                                            style={{ padding: '4px 8px', fontSize: '12px', border: '1px solid #cbd5e0', borderRadius: '4px' }}
                                                                            value={q.dropdown_options || ''}
                                                                            onChange={(e) => updateQuestion(sIdx, qIdx, 'dropdown_options', e.target.value)}
                                                                        />
                                                                    )}
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                                                        <input type="checkbox" checked={!!q.show_obs} onChange={(e) => updateQuestion(sIdx, qIdx, 'show_obs', e.target.checked)} /> Obs.
                                                                    </label>
                                                                    <div style={{ width: '1px', height: '20px', backgroundColor: '#cbd5e0', margin: '0 5px' }}></div>
                                                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', color: '#2b6cb0', fontWeight: 'bold' }}>
                                                                        <input type="checkbox" checked={!!q.is_multiplier} onChange={(e) => updateQuestion(sIdx, qIdx, 'is_multiplier', e.target.checked)} />
                                                                        Multiplicar Linhas
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    <button type="button" className="btn-secondary" style={{ fontSize: '12px', padding: '5px 10px', marginTop: '10px' }} onClick={() => addQuestion(sIdx)}>
                                                        + Adicionar Pergunta
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default function AdminFichas() { return <OrcamentoFeedbackProvider><AdminFichasContent /></OrcamentoFeedbackProvider>; }
